/**
 * Toast notification helpers using Sonner
 * Provides consistent toast notifications across the application
 */

import { toast } from "sonner";

/**
 * Show a success toast notification
 * @param {string} message - Main message to display
 * @param {string} [description] - Optional detailed description
 * @param {number} [duration=5000] - Duration in milliseconds
 */
export const showSuccess = (message, description, duration = 5000) => {
  toast.success(message, {
    description,
    duration,
  });
};

/**
 * Show an error toast notification
 * @param {string} message - Main error message
 * @param {string} [description] - Optional error details
 * @param {number} [duration=5000] - Duration in milliseconds
 */
export const showError = (message, description, duration = 5000) => {
  toast.error(message, {
    description,
    duration,
  });
};

/**
 * Show a warning toast notification
 * @param {string} message - Warning message
 * @param {string} [description] - Optional warning details
 * @param {number} [duration=5000] - Duration in milliseconds
 */
export const showWarning = (message, description, duration = 5000) => {
  toast.warning(message, {
    description,
    duration,
  });
};

/**
 * Show an info toast notification
 * @param {string} message - Info message
 * @param {string} [description] - Optional info details
 * @param {number} [duration=5000] - Duration in milliseconds
 */
export const showInfo = (message, description, duration = 5000) => {
  toast.info(message, {
    description,
    duration,
  });
};

/**
 * Show a toast with a custom action button
 * @param {string} message - Main message
 * @param {Object} options - Toast options
 * @param {string} options.description - Optional description
 * @param {string} options.actionLabel - Button label
 * @param {Function} options.actionOnClick - Button click handler
 * @param {'success'|'error'|'warning'|'info'} [options.type='info'] - Toast type
 * @param {number} [options.duration=5000] - Duration in milliseconds
 */
export const showToastWithAction = (
  message,
  {
    description,
    actionLabel,
    actionOnClick,
    type = "info",
    duration = 5000,
  }
) => {
  const toastFn = toast[type] || toast.info;

  toastFn(message, {
    description,
    action: {
      label: actionLabel,
      onClick: actionOnClick,
    },
    duration,
  });
};

/**
 * Show a promise-based toast (loading → success/error)
 * Useful for async operations
 * @param {Promise} promise - Promise to track
 * @param {Object} messages - Messages for different states
 * @param {string} messages.loading - Loading message
 * @param {string|Function} messages.success - Success message (can be function receiving result)
 * @param {string|Function} messages.error - Error message (can be function receiving error)
 */
export const showPromiseToast = (promise, messages) => {
  return toast.promise(promise, messages);
};

/**
 * Dismiss all active toasts
 */
export const dismissAllToasts = () => {
  toast.dismiss();
};

/**
 * Dismiss a specific toast by ID
 * @param {string} toastId - Toast ID to dismiss
 */
export const dismissToast = (toastId) => {
  toast.dismiss(toastId);
};
